import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { KeyRound, Lock, Plus, ShieldAlert, UserCog, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { CopyButton } from '@/components/catalog/CatalogParts';
import { AccessBadge, OnlineAccountStatusBadge } from '@/components/catalog/OnlineAccountBadges';
import { OnlineAccountDialog } from '@/components/catalog/OnlineAccountDialog';
import { PersonAccountAccessDialog } from '@/components/catalog/PersonAccountAccessDialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/app/AuthProvider';
import { useOnlineAccountsCatalog } from '@/features/onlineAccounts/hooks';
import {
  ONLINE_ACCOUNT_CATEGORIES,
  ONLINE_ACCOUNT_CATEGORY_LABELS,
  ONLINE_ACCOUNT_STATUSES,
  ONLINE_ACCOUNT_STATUS_LABELS,
  canShareAccount,
  hasLiveAccess,
  loginLabel,
  type OnlineAccountRow,
} from '@/features/onlineAccounts/model';
import { websiteLabel } from '@/features/software/model';
import { formatDateOnly, formatPhone } from '@/lib/formatters';
import { isSuperAdmin } from '@/lib/permissions';

type Sharing = 'private' | 'shared';

const SHARING_LABELS: Record<Sharing, string> = {
  private: 'Super admins only',
  shared: 'Shared',
};

function sharingOf(row: OnlineAccountRow): Sharing {
  return row.people.some(hasLiveAccess) ? 'shared' : 'private';
}

function peopleCount(n: number): string {
  return `${n} ${n === 1 ? 'person' : 'people'}`;
}

function loginSummary(row: OnlineAccountRow): string {
  const admins = row.logins.filter((l) => l.role === 'admin').length;
  const users = row.logins.length - admins;
  const parts: string[] = [];
  if (admins) parts.push(`${admins} admin`);
  if (users) parts.push(`${users} ${users === 1 ? 'user' : 'users'}`);
  return parts.join(' · ') || 'No logins';
}

/**
 * Every website and portal the company signs in to. Super admins see all
 * of them; everyone else sees only what has been shared with them.
 */
export function OnlineAccountsPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { rows, people, shareable, profilesById, isLoading, error } = useOnlineAccountsCatalog();
  const [creating, setCreating] = useState(false);
  const [byPerson, setByPerson] = useState(false);

  const isOwner = isSuperAdmin(role);
  const sharable = useMemo(() => rows.filter((r) => canShareAccount(r.myAccess)), [rows]);
  const canShareAny = sharable.length > 0;

  const summary = useMemo(() => {
    const active = rows.filter((r) => r.status === 'active');
    const logins = rows.flatMap((r) => r.logins);
    const missing = active.flatMap((r) => r.logins).filter((l) => !l.has_password).length;
    const sharedWith = new Set(
      sharable.flatMap((r) => r.people.filter(hasLiveAccess).map((p) => p.profileId)),
    ).size;
    const privateCount = sharable.filter((r) => sharingOf(r) === 'private').length;
    return {
      active: active.length,
      closed: rows.length - active.length,
      logins: logins.length,
      users: logins.filter((l) => l.role === 'user').length,
      missing,
      sharedWith,
      privateCount,
    };
  }, [rows, sharable]);

  const columns = useMemo<ColumnDef<OnlineAccountRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Account',
        size: 220,
        minSize: 160,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{row.original.name}</div>
            {row.original.website && (
              <div className="truncate text-xs text-muted-foreground">
                {websiteLabel(row.original.website)}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'category',
        accessorFn: (row) => row.category,
        header: 'Category',
        size: 170,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant="secondary" className="whitespace-nowrap">
            {ONLINE_ACCOUNT_CATEGORY_LABELS[row.original.category]}
          </Badge>
        ),
      },
      {
        id: 'admin_login',
        accessorFn: (row) =>
          row.logins.map((l) => [l.username, l.email, l.holder_name].filter(Boolean).join(' ')).join(' '),
        header: 'Admin login',
        size: 220,
        cell: ({ row }) => {
          const login = row.original.adminLogin ?? row.original.logins[0];
          if (!login) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="inline-flex max-w-full items-center gap-1">
              <span className="truncate">{loginLabel(login)}</span>
              <CopyButton value={loginLabel(login)} label="Login" />
            </span>
          );
        },
      },
      {
        id: 'logins',
        accessorFn: (row) => row.logins.length,
        header: 'Logins',
        size: 150,
        cell: ({ row }) => {
          const missing = row.original.logins.filter((l) => !l.has_password).length;
          return (
            <div className="min-w-0">
              <div className="whitespace-nowrap text-sm text-foreground">
                {loginSummary(row.original)}
              </div>
              {missing > 0 && (
                <div className="whitespace-nowrap text-xs text-muted-foreground">
                  {missing} without a password
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: 'phone',
        accessorFn: (row) => row.phone ?? '',
        header: 'Phone',
        size: 150,
        cell: ({ row }) =>
          row.original.phone ? (
            <span className="whitespace-nowrap">{formatPhone(row.original.phone)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'opened_on',
        accessorFn: (row) => row.opened_on ?? '',
        header: 'Created',
        size: 120,
        cell: ({ row }) =>
          row.original.opened_on ? (
            <span className="whitespace-nowrap">{formatDateOnly(row.original.opened_on)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'opened_by',
        accessorFn: (row) => row.openedByName ?? '',
        header: 'Created by',
        size: 160,
        cell: ({ row }) =>
          row.original.openedByName ? (
            <span className="truncate">{row.original.openedByName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'sharing',
        accessorFn: (row) => (canShareAccount(row.myAccess) ? sharingOf(row) : 'shared'),
        header: 'Access',
        size: 170,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <AccessCell row={row.original} />,
      },
      {
        id: 'status',
        accessorFn: (row) => row.status,
        header: 'Status',
        size: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <OnlineAccountStatusBadge status={row.original.status} />,
      },
      {
        id: 'account_number',
        accessorFn: (row) => row.account_number ?? '',
        header: 'Account #',
        size: 140,
        cell: ({ row }) =>
          row.original.account_number ?? <span className="text-muted-foreground">—</span>,
      },
    ],
    [],
  );

  const filters: FacetedFilter[] = useMemo(
    () => [
      {
        columnId: 'category',
        title: 'Category',
        options: ONLINE_ACCOUNT_CATEGORIES.map((value) => ({
          value,
          label: ONLINE_ACCOUNT_CATEGORY_LABELS[value],
        })),
      },
      {
        columnId: 'status',
        title: 'Status',
        options: ONLINE_ACCOUNT_STATUSES.map((value) => ({
          value,
          label: ONLINE_ACCOUNT_STATUS_LABELS[value],
        })),
      },
      ...(canShareAny
        ? [
            {
              columnId: 'sharing',
              title: 'Access',
              options: (['private', 'shared'] as Sharing[]).map((value) => ({
                value,
                label: SHARING_LABELS[value],
              })),
            },
          ]
        : []),
    ],
    [canShareAny],
  );

  const emptyState = isOwner ? (
    <EmptyState
      icon={KeyRound}
      title="No accounts yet"
      detail="Add the websites and portals the company signs in to, with each admin and user login."
      action={
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          Add account
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={Lock}
      title="No accounts shared with you"
      detail="Accounts are private. A super admin can share the ones you need."
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Catalog · Software"
        title="Accounts"
        meta={
          isOwner
            ? 'Online accounts and their logins. Only super admins see an account until it’s shared.'
            : 'Online accounts that have been shared with you.'
        }
        actions={
          canShareAny || isOwner ? (
            <div className="flex flex-wrap gap-2">
              {canShareAny && (
                <Button variant="outline" onClick={() => setByPerson(true)}>
                  <UserCog className="h-4 w-4" />
                  Access by person
                </Button>
              )}
              {isOwner && (
                <Button onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" />
                  Add account
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {(isLoading || rows.length > 0) && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Accounts"
            value={String(summary.active)}
            loading={isLoading}
            icon={<KeyRound className="h-4 w-4" />}
            tone="neutral"
            hint={summary.closed ? `Active · ${summary.closed} closed` : 'Active'}
          />
          <MetricCard
            label="Logins"
            value={String(summary.logins)}
            loading={isLoading}
            icon={<Users className="h-4 w-4" />}
            tone="neutral"
            hint={`${summary.users} additional ${summary.users === 1 ? 'user' : 'users'}`}
          />
          <MetricCard
            label="Missing passwords"
            value={String(summary.missing)}
            loading={isLoading}
            icon={<ShieldAlert className="h-4 w-4" />}
            tone={summary.missing > 0 ? 'negative' : 'neutral'}
            hint={summary.missing > 0 ? 'Logins on active accounts with no password saved' : 'Every login has one'}
          />
          {canShareAny && (
            <MetricCard
              label="Shared with"
              value={`${summary.sharedWith} ${summary.sharedWith === 1 ? 'person' : 'people'}`}
              loading={isLoading}
              icon={<Lock className="h-4 w-4" />}
              tone="neutral"
              hint={`${summary.privateCount} ${summary.privateCount === 1 ? 'account is' : 'accounts are'} super admins only`}
            />
          )}
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {error ? (
            <EmptyState
              icon={KeyRound}
              title="Could not load accounts"
              detail={
                error instanceof Error
                  ? error.message
                  : 'Apply the latest database migration and try again.'
              }
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/catalog/software/accounts/${row.id}`)}
              searchPlaceholder="Search accounts, usernames, emails, or people…"
              facetedFilters={filters}
              tableId="catalog-online-accounts"
              initialColumnVisibility={{ account_number: false, phone: false }}
              initialPageSize={50}
              rowClassName={(row) => (row.status === 'closed' ? 'opacity-60' : undefined)}
              mobileCard={{
                title: (row) => row.name,
                subtitle: (row) => (row.website ? websiteLabel(row.website) : undefined),
                badges: (row) => [
                  <Badge key="category" variant="secondary">
                    {ONLINE_ACCOUNT_CATEGORY_LABELS[row.category]}
                  </Badge>,
                  row.status === 'closed' ? (
                    <OnlineAccountStatusBadge key="status" status={row.status} />
                  ) : null,
                  canShareAccount(row.myAccess) ? null : row.myAccess ? (
                    <AccessBadge key="access" access={row.myAccess} />
                  ) : null,
                ],
                meta: (row) => [
                  {
                    label: 'Admin',
                    value: row.adminLogin ? loginLabel(row.adminLogin) : '—',
                  },
                  { label: 'Logins', value: loginSummary(row) },
                  ...(row.opened_on
                    ? [{ label: 'Created', value: formatDateOnly(row.opened_on) }]
                    : []),
                  ...(canShareAccount(row.myAccess)
                    ? [
                        {
                          label: 'Access',
                          value:
                            sharingOf(row) === 'private'
                              ? SHARING_LABELS.private
                              : peopleCount(row.people.filter(hasLiveAccess).length),
                        },
                      ]
                    : []),
                ],
                dimmed: (row) => row.status === 'closed',
              }}
              emptyState={emptyState}
            />
          )}
        </CardContent>
      </Card>

      <OnlineAccountDialog
        open={creating}
        account={null}
        people={people}
        canDelete={false}
        onOpenChange={setCreating}
        onSaved={(id) => navigate(`/catalog/software/accounts/${id}`)}
      />
      <PersonAccountAccessDialog
        open={byPerson}
        rows={sharable}
        shareable={shareable}
        profilesById={profilesById}
        isOwner={isOwner}
        onOpenChange={setByPerson}
      />
    </div>
  );
}

function AccessCell({ row }: { row: OnlineAccountRow }) {
  if (!canShareAccount(row.myAccess)) {
    return row.myAccess ? <AccessBadge access={row.myAccess} /> : null;
  }
  const active = row.people.filter(hasLiveAccess);
  if (active.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
        <Lock className="h-3.5 w-3.5" />
        {SHARING_LABELS.private}
      </span>
    );
  }
  return <span className="whitespace-nowrap text-foreground">{peopleCount(active.length)}</span>;
}
